import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { groupsAPI } from '../services/api';
import { AppLayout } from '../components/layout/AppLayout';
import { Header } from '../components/layout/Header';
import { Card } from '../components/ui/Card';

interface BalanceSummary {
  totalYouOwe: number;
  totalYouAreOwed: number;
  netBalance: number;
}

interface GroupBalance {
  groupId: string;
  groupName: string;
  youOwe: number;
  youAreOwed: number;
  netBalance: number;
}

const Balances: React.FC = () => {
  const navigate = useNavigate();
  const [balances, setBalances] = useState<GroupBalance[]>([]);
  const [summary, setSummary] = useState<BalanceSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBalances();
  }, []);

  const loadBalances = async () => {
    try {
      const data = await groupsAPI.getAllBalances();
      setBalances(data.balances);
      setSummary(data.summary);
    } catch (error) {
      console.error('Failed to load balances:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AppLayout>
      <Header title="Balances" />

      <main className="p-4 pb-24 space-y-6">
        {/* Summary Card */}
        {summary && (
          <Card className={`${
            summary.netBalance >= 0 ? 'bg-green-50 border-green-100' : 'bg-red-50 border-red-100'
          }`}>
            <div className="text-center">
              <p className="text-sm text-slate-600 mb-1">Overall Balance</p>
              <p className={`text-3xl font-bold ${
                summary.netBalance >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {summary.netBalance >= 0 ? '+' : ''}₹{Math.abs(summary.netBalance).toLocaleString('en-IN')}
              </p>
              <p className="text-sm text-slate-500 mt-2">
                {summary.netBalance >= 0
                  ? 'You are owed money overall'
                  : 'You owe money overall'}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-6 pt-4 border-t border-slate-200">
              <div className="text-center">
                <p className="text-xs text-slate-500 mb-1">You owe</p>
                <p className="text-lg font-semibold text-red-600">
                  ₹{summary.totalYouOwe.toLocaleString('en-IN')}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-slate-500 mb-1">You are owed</p>
                <p className="text-lg font-semibold text-green-600">
                  ₹{summary.totalYouAreOwed.toLocaleString('en-IN')}
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Group Balances */}
        <section>
          <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-3 px-1">
            By Group
          </h3>

          {balances.length === 0 ? (
            <Card>
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">All settled up!</h3>
                <p className="text-sm text-slate-500">
                  You don't have any pending balances
                </p>
              </div>
            </Card>
          ) : (
            <div className="space-y-3">
              {balances.map((balance) => (
                <Card
                  key={balance.groupId}
                  hover
                  onClick={() => navigate(`/groups/${balance.groupId}`)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <span className="text-xl">👥</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-slate-900 truncate">{balance.groupName}</h4>
                      <div className="flex gap-4 text-sm">
                        {balance.youOwe > 0 && (
                          <span className="text-red-600">
                            You owe ₹{balance.youOwe.toLocaleString('en-IN')}
                          </span>
                        )}
                        {balance.youAreOwed > 0 && (
                          <span className="text-green-600">
                            You get ₹{balance.youAreOwed.toLocaleString('en-IN')}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className={`text-right ${balance.netBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      <p className="font-bold">
                        {balance.netBalance >= 0 ? '+' : ''}₹{Math.abs(balance.netBalance).toLocaleString('en-IN')}
                      </p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </section>
      </main>
    </AppLayout>
  );
};

export default Balances;
